import { Component } from '@theme/component';
import {
  fetchConfig,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartSectionRestoredEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Set<Promise<void>>} */
  static #pendingQuantityUpdates = new Set();

  /**
   * Includes quantity mutations, their section renders, and control cleanup across all cart instances.
   * @returns {ReadonlyArray<Promise<void>>} A snapshot that cannot modify the shared pending set.
   */
  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingQuantityUpdates);
  }

  /** Quantity requests share one queue across the cart page and drawer. */
  static #quantityQueue = Promise.resolve();
  static #quantityRevision = 0;
  static #cartStateUncertain = false;

  /** @type {Map<string, { timer: ReturnType<typeof setTimeout>, finish: () => void }>} */
  #quantityChanges = new Map();

  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /**
   * Refreshes this section when a cart AJAX response cannot include rendered section HTML.
   * @param {Object} [options] - Additional section morph options.
   * @param {boolean} [options.injectStylesheet]
   */
  async #refreshCartSection(options = {}) {
    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        ...options,
      });
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      if (error?.name === 'AbortError') return;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to refresh cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const isExternalCartUpdate = this.#isExternalCartUpdate(event);

    event.promise
      ?.then(({ detail }) => {
        if (!isExternalCartUpdate) return;

        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
    this.#syncCheckoutState();
    this.addEventListener('submit', this.#guardCheckout, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
    for (const key of this.#quantityChanges.keys()) this.#cancelQuantityChange(key);
    this.removeEventListener('submit', this.#guardCheckout, true);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncCheckoutState();
  }

  /**
   * Re-renders the cart section when the page is restored from the back/forward cache.
   *
   * A restored page replays a frozen DOM, so the line items still show whatever the cart
   * held before the shopper navigated away. `cart-icon.js` self-corrects its count bubble
   * on this same event, which is why the bubble and the cart body disagree until a reload.
   *
   * `persisted` alone does not cover Chrome. Chrome refuses the bfcache for any document holding
   * the Sign in with Shop iframe, `/services/login_with_shop/authorize` inside the shadow root of
   * `shopify-account > shop-login`, and every product page carries one. A shopper's Back there
   * rebuilds the document from the HTTP disk cache instead: `persisted` is false and the frozen
   * cart markup renders anyway. Only the navigation entry separates that from an ordinary page
   * view, so it is the second channel. Safari and Firefox do restore the same page and arrive
   * through `persisted`. Measured on os3 across all three browsers, 2026-09-03.
   *
   * Both channels are still needed. A restored document keeps its ORIGINAL navigation entry in
   * Chrome and Safari, reporting `navigate`, so `back_forward` alone would miss every real
   * restore. And `pageshow` fires after `load` on every ordinary navigation, so dropping both
   * guards would put a section fetch on every page view.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    const navigationType = CartItemsComponent.#isNavigationTiming(navigationEntry) ? navigationEntry.type : undefined;
    if (!event.persisted && navigationType !== 'back_forward') return;
    // `section_id` is optional in `snippets/cart-items-component.liquid` and the `sectionId`
    // getter throws without it. Every other caller reads it inside a promise chain that
    // already has a catch; this one runs straight off a window listener, so it would escape.
    if (!this.dataset.sectionId) return;

    // `cache: false` on both paths. A bfcache restore never fires `load`, so the renderer's cache
    // still holds the pre-navigation copy. A disk-cache Back does fire `load` and seeds the cache
    // from the stale document it just rebuilt. Either way the cached copy is the same stale HTML
    // the DOM already shows.
    sectionRenderer
      .renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        // Empty → non-empty adds the cart summary markup, which carries its own stylesheet.
        injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
      })
      .then(() => {
        // Morph swaps the quantity selectors in place, so they never reconnect and their
        // min/max button states stay bound to the pre-restore quantities. A line restored to its
        // minimum would otherwise keep minus enabled: a wrong affordance and a wasted cart mutation.
        this.#updateCartQuantitySelectorButtonStates();
        // No drawer-open event fires on a restore, so the sticky summary measurement has to
        // be driven from here. `cart-drawer-component` listens for this.
        this.dispatchEvent(new CartSectionRestoredEvent());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] bfcache restore render failed:', error);
      });
  };

  /**
   * Capture the Shopify line key before a section morph can change the row indices.
   * Repeated changes to one line coalesce; changes to other lines stay independent.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent) || !(event.target instanceof Node) || !this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;
    const row = this.refs.cartItemRows?.[line - 1];
    const id = row?.dataset.key;
    if (!id) return;

    this.#cancelQuantityChange(id);
    let complete = () => {};
    /** @type {Promise<void>} */
    const pending = new Promise((resolve) => { complete = () => resolve(); });
    CartItemsComponent.#pendingQuantityUpdates.add(pending);
    const finish = () => {
      CartItemsComponent.#pendingQuantityUpdates.delete(pending);
      complete();
      CartItemsComponent.#syncCheckoutStates();
    };
    CartItemsComponent.#syncCheckoutStates();
    const timer = setTimeout(() => {
      this.#quantityChanges.delete(id);
      this.updateQuantity({ id, quantity, action: quantity === 0 ? 'clear' : 'change' }).finally(finish);
    }, 300);
    this.#quantityChanges.set(id, { timer, finish });
    /** @type {TextComponent | null} */ (row.querySelector('text-component'))?.shimmer();
  };

  /** @param {string} id */
  #cancelQuantityChange(id) {
    const pending = this.#quantityChanges.get(id);
    if (!pending) return;
    clearTimeout(pending.timer);
    pending.finish();
    this.#quantityChanges.delete(id);
  }

  /** @param {number} line */
  onLineItemRemove(line) {
    const id = this.refs.cartItemRows?.[line - 1]?.dataset.key;
    if (!id) return;
    this.#cancelQuantityChange(id);
    // Keep the row (including nested lines and the last item) until Shopify confirms.
    // Restoring an old DOM snapshot after a lost response could resurrect a removed item.
    return this.updateQuantity({ id, quantity: 0, action: 'clear' });
  }

  /**
   * @param {{ id?: string, line?: number, quantity: number, action: string }} config
   * @returns {Promise<void>}
   */
  updateQuantity(config) {
    const id = config.id ?? this.refs.cartItemRows?.[(config.line ?? 0) - 1]?.dataset.key;
    if (!id || !Number.isInteger(config.quantity) || config.quantity < 0) return Promise.resolve();
    CartItemsComponent.#quantityRevision++;
    return this.#queueCartTask(async () => {
      if (!this.isConnected) return;
      await this.#performQuantityUpdate({ ...config, id });
    });
  }

  /** Serialize mutations and their reconciliation renders, not just the POSTs.
   * @param {() => Promise<void>} task
   */
  #queueCartTask(task) {
    const update = CartItemsComponent.#quantityQueue.then(task);
    // A failed operation must never poison later user actions.
    CartItemsComponent.#quantityQueue = update.catch(() => {});
    CartItemsComponent.#pendingQuantityUpdates.add(update);
    CartItemsComponent.#syncCheckoutStates();
    return update.finally(() => {
      CartItemsComponent.#pendingQuantityUpdates.delete(update);
      CartItemsComponent.#syncCheckoutStates();
    });
  }

  /** @param {{ id: string, quantity: number, action: string }} config */
  async #performQuantityUpdate(config) {
    const marker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const deferredUpdate = CartLinesUpdateEvent.createPromise();
    // Recovery can fail before the event is dispatched and has any subscribers.
    deferredUpdate.promise.catch(() => {});
    const components = this.#cartComponents();
    let validationError = '';
    try {
      for (const component of components) component.#disableCartItems();
      if (CartItemsComponent.#cartStateUncertain) await this.#reconcileCart();
      const sections = [...new Set(this.#cartComponents().map((component) => component.sectionId))];
      for (const sectionId of sections) sectionRenderer.abortRender(sectionId);
      this.refs.cartTotal?.shimmer();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: config.action === 'change' && config.quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: config.id, quantity: config.quantity }],
        promise: deferredUpdate.promise,
      }));

      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
        body: JSON.stringify({ id: config.id, quantity: config.quantity, sections: sections.join(','), sections_url: window.location.pathname }),
      }));
      const cart = JSON.parse(await response.text());
      if (!response.ok || cart.errors || !Array.isArray(cart.items)) {
        if (cart.errors || response.status === 422) {
          validationError = typeof cart.errors === 'string' ? cart.errors : cart.description || '';
        }
        throw new Error(validationError || cart.description || 'Failed to update cart');
      }

      // Shopify can accept the mutation while returning null sections. Read the
      // current cart instead of replaying the POST or rendering a partial snapshot.
      const complete = sections.every((sectionId) => typeof cart.sections?.[sectionId] === 'string');
      const currentCart = complete ? cart : await this.#readCartSnapshot();
      await this.#renderCartSnapshot(currentCart);
      deferredUpdate.resolve(this.#cartUpdateResult(currentCart));
    } catch (error) {
      let reconciled = false;
      let restoredCart;
      try {
        restoredCart = await this.#reconcileCart();
        this.#publishCartSnapshot(restoredCart);
        reconciled = true;
      } catch (recoveryError) {
        CartItemsComponent.#cartStateUncertain = true;
        console.warn('[cart-items] Cart recovery failed:', recoveryError);
      }
      if (validationError && restoredCart) {
        deferredUpdate.resolve({ ...this.#cartUpdateResult(restoredCart), userErrors: [{ code: 'INVALID', field: [], message: validationError }] });
      } else {
        deferredUpdate.reject(error);
        this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Failed to update cart', code: 'SERVICE_UNAVAILABLE' }));
      }
      for (const component of this.#cartComponents()) {
        component.#syncCheckoutState();
        component.#showCartError(reconciled ? validationError || Theme.translations.cart_update_error : Theme.translations.cart_update_unavailable);
      }
    } finally {
      for (const component of new Set([...components, ...this.#cartComponents()])) {
        resetShimmer(component);
        component.#enableCartItems();
      }
      cartPerformance.measureFromMarker(marker);
    }
  }

  /** @returns {CartItemsComponent[]} */
  #cartComponents() {
    return [...document.querySelectorAll('cart-items-component')].filter(
      (component) => component instanceof CartItemsComponent && component.dataset.sectionId
    );
  }

  /** Read-only recovery: a lost response does not tell us whether the POST succeeded. */
  async #readCartSnapshot() {
    const sections = [...new Set(this.#cartComponents().map((component) => component.sectionId))];
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', sections.join(','));
    const options = { headers: { Accept: 'application/json' }, credentials: /** @type {RequestCredentials} */ ('same-origin'), cache: /** @type {RequestCache} */ ('no-store') };
    const [cartResponse, sectionsResponse] = await Promise.all([
      fetch(`${Theme.routes.cart_url}.js`, options),
      fetch(url, options),
    ]);
    if (!cartResponse.ok || !sectionsResponse.ok) throw new Error('Failed to refresh cart');
    const [cart, renderedSections] = await Promise.all([cartResponse.json(), sectionsResponse.json()]);
    if (!Array.isArray(cart.items) || !Number.isFinite(cart.item_count) || !sections.every((id) => typeof renderedSections?.[id] === 'string')) {
      throw new Error('Incomplete cart refresh');
    }
    return { ...cart, sections: renderedSections };
  }

  async #reconcileCart() {
    const cart = await this.#readCartSnapshot();
    await this.#renderCartSnapshot(cart);
    return cart;
  }

  /** @param {any} cart */
  async #renderCartSnapshot(cart) {
    const sections = new Map(this.#cartComponents().map((component) => [component.sectionId, component.isDrawer]));
    for (const [sectionId, isDrawer] of sections) {
      if (typeof cart.sections?.[sectionId] !== 'string') throw new Error('Missing cart section');
      sectionRenderer.abortRender(sectionId);
      await morphSection(sectionId, cart.sections[sectionId], { mode: isDrawer ? 'hydration' : 'full', injectStylesheet: true });
    }
    CartItemsComponent.#cartStateUncertain = false;
    this.#updateQuantitySelectors(cart);
    for (const component of this.#cartComponents()) {
      component.querySelector('[data-cart-update-error]')?.remove();
      component.#syncCheckoutState();
      component.#updateCartQuantitySelectorButtonStates();
    }
  }

  /** @param {any} cart */
  #cartUpdateResult(cart) {
    return {
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: cart.sections, items: cart.items, itemCount: cart.item_count, source: 'cart-items-component', didError: false },
    };
  }

  /** @param {any} cart */
  #publishCartSnapshot(cart) {
    const target = this.isConnected ? this : this.#cartComponents()[0];
    // A GET restoration is not a Shopify cart mutation. Notify theme consumers
    // once with the confirmed count, and let every drawer remeasure its summary.
    for (const component of this.#cartComponents()) {
      component.dispatchEvent(new CartSectionRestoredEvent(component === target ? cart : undefined));
    }
  }

  /** @param {string} message */
  #showCartError(message) {
    let alert = this.querySelector('[data-cart-update-error]');
    if (!alert) {
      alert = document.createElement('p');
      alert.setAttribute('data-cart-update-error', '');
      alert.setAttribute('role', 'alert');
      this.prepend(alert);
    }
    alert.textContent = message;
  }

  static #syncCheckoutStates() {
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof CartItemsComponent) component.#syncCheckoutState();
    }
  }

  /** @param {SubmitEvent} event */
  #guardCheckout = (event) => {
    if (!(event.target instanceof HTMLFormElement) || event.target.id !== 'cart-form') return;
    if (CartItemsComponent.#cartStateUncertain || CartItemsComponent.#pendingQuantityUpdates.size > 0) event.preventDefault();
  };

  #syncCheckoutState() {
    const uncertain = CartItemsComponent.#cartStateUncertain;
    const blocked = uncertain || CartItemsComponent.#pendingQuantityUpdates.size > 0;
    for (const button of this.querySelectorAll('button[name="checkout"]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      if (blocked) {
        if (!button.hasAttribute('data-cart-checkout-disabled')) button.dataset.cartCheckoutDisabled = String(button.disabled);
        button.disabled = true;
      } else if (button.hasAttribute('data-cart-checkout-disabled')) {
        button.disabled = button.dataset.cartCheckoutDisabled === 'true';
        delete button.dataset.cartCheckoutDisabled;
      }
    }
    for (const express of this.querySelectorAll('.additional-checkout-buttons')) {
      if (!(express instanceof HTMLElement)) continue;
      if (blocked) {
        if (!express.hasAttribute('data-cart-checkout-inert')) express.dataset.cartCheckoutInert = String(express.hasAttribute('inert'));
        express.setAttribute('inert', '');
      } else if (express.hasAttribute('data-cart-checkout-inert')) {
        express.toggleAttribute('inert', express.dataset.cartCheckoutInert === 'true');
        delete express.dataset.cartCheckoutInert;
      }
    }
    if (uncertain) this.#showCartError(Theme.translations.cart_update_unavailable);
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    // Our own queue renders every cart instance before resolving the event.
    if (event.target instanceof CartItemsComponent) return;
    const revision = CartItemsComponent.#quantityRevision;
    const wasPending = CartItemsComponent.#pendingQuantityUpdates.size > 0;

    event.promise
      ?.then(({ detail }) => this.#queueCartTask(async () => {
        if (!this.isConnected) return;
        if (wasPending || revision !== CartItemsComponent.#quantityRevision || CartItemsComponent.#cartStateUncertain) {
          // Reconciliation itself shares the queue. A new mutation must not race
          // a recovery GET and then get overwritten by its older section HTML.
          try {
            const cart = await this.#reconcileCart();
            this.#publishCartSnapshot(cart);
          } catch (error) {
            CartItemsComponent.#cartStateUncertain = true;
            CartItemsComponent.#syncCheckoutStates();
            this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Failed to refresh cart', code: 'SERVICE_UNAVAILABLE' }));
          }
          return;
        }
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            await startViewTransition(() => morphSection(this.sectionId, cartItemsHtml, morphOptions), ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          await this.#refreshCartSection(morphOptions);
        }
      }))
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  /**
   * `getEntriesByType` is typed as the `PerformanceEntry` base, which has no `type`, and the DOM
   * lib has no per-string overload for `'navigation'`. `entryType` is the spec's discriminant and
   * lives on the base, so this narrows at runtime instead of asserting the way a cast would.
   *
   * @param {PerformanceEntry | undefined} entry
   * @returns {entry is PerformanceNavigationTiming}
   */
  static #isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
